import { db } from "../firebaseClient"
import { doc, getDoc, setDoc, updateDoc, arrayUnion, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { generateLocalId } from "./courseHelpers"
import { logVerificationEvent } from "./verificationEventHelpers"
import { dispatchWebhook } from "./webhookHelpers"
import { logPlatformEvent } from "./eventHelpers"

// Certificate IDs are human-readable and used directly as the Firestore document ID,
// so public verification is a single getDoc — no query needed.
export const generateCertificateId = (orgId) => {
  const suffix = generateLocalId().toUpperCase()
  return `NESO-${orgId}-${suffix}`
}

// Issues a certificate once a learner passes a course's capstone with certification
// enabled. Caller (course-capstone page) is responsible for checking the enrollment's
// certificateId first, so this never gets called twice for the same enrollment.
export const issueCertificate = async ({
  userId,
  userName,
  courseId,
  courseTitle,
  orgId,
  orgName,
  orgVerified,
  score,
  templateId,
  template,
}) => {
  if (!userId || !courseId || !orgId) {
    return { certificate: null, error: { message: "Missing required fields" } }
  }

  try {
    const certId = generateCertificateId(orgId)
    const certRef = doc(db, "certificates", certId)

    // templateSnapshot locks in the template's design (colors, logo, layout,
    // issuer, skills) at the moment of issuance — so a later edit to the
    // template in Credential Templates never silently changes a credential
    // that's already been issued and possibly shared or verified externally.
    const certificateData = {
      certId,
      userId,
      issuedTo: userName || "Nesolearn Learner",
      courseId,
      courseTitle,
      orgId,
      issuedBy: orgName || orgId,
      issuedByVerified: !!orgVerified,
      score: score || 0,
      status: "valid",
      issueDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      templateId: templateId || null,
      templateSnapshot: template
        ? {
            name: template.name || "",
            type: template.type || "certificate",
            design: template.design || null,
            issuer: template.issuer || null,
            skills: template.skills || [],
          }
        : null,
    }

                await setDoc(certRef, certificateData)
    dispatchWebhook(orgId, "credential.issued", { certId, userId, courseId, courseTitle }).catch(() => {})
    logPlatformEvent({
      type: "CREDENTIAL_ISSUED",
      actorUid: userId,
      actorType: "system",
      orgId,
      targetPath: `certificates/${certId}`,
      metadata: { certId, courseId, courseTitle, score: score || 0 },
    })

    // Best-effort: seed the learner's public credential list (Skills Passport foundation).
    // issuedAt is a plain string, not serverTimestamp() — arrayUnion doesn't allow that sentinel inside array elements.
    const profileRef = doc(db, "learnerProfiles", userId)
    await setDoc(
      profileRef,
      {
        uid: userId,
        updatedAt: serverTimestamp(),
        credentials: arrayUnion({
          certId,
          orgId,
          orgName: orgName || orgId,
          orgVerified: !!orgVerified,
          courseId,
          courseTitle,
          issuedAt: certificateData.issueDate,
        }),
      },
      { merge: true }
    ).catch(() => {})

    return { certificate: certificateData, error: null }
  } catch (error) {
    return { certificate: null, error }
  }
}

// Public lookup for the /verify/:certId page — no auth required. Also logs a
// verification event (fire-and-forget) so Verification Activity reflects
// real external checks, not just issuance.
export const getCertificate = async (certId, { logEvent = true, source = "public" } = {}) => {
  try {
    const certRef = doc(db, "certificates", certId)
    const snap = await getDoc(certRef)
    if (!snap.exists()) return { certificate: null, error: null }
    const certificate = snap.data()
    if (logEvent) {
      logVerificationEvent({ certId, orgId: certificate?.orgId, source }).catch(() => {})
    }
    return { certificate, error: null }
  } catch (error) {
    return { certificate: null, error }
  }
}

// Revokes a previously issued certificate — reversible via reinstateCertificate,
// since institutions sometimes revoke in error or pending review.
export const revokeCertificate = async (certId, orgId = null) => {
  try {
    await updateDoc(doc(db, "certificates", certId), { status: "revoked", revokedAt: serverTimestamp() })
    logPlatformEvent({
      type: "CREDENTIAL_REVOKED",
      actorType: "org_admin",
      orgId,
      targetPath: `certificates/${certId}`,
      severity: "warning",
      metadata: { certId },
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const reinstateCertificate = async (certId) => {
  try {
    await updateDoc(doc(db, "certificates", certId), { status: "valid", revokedAt: null })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Certificates issued by a given org (for the dashboard's Certificates tab)
export const listOrgCertificates = async (orgId) => {
  try {
    const certsRef = collection(db, "certificates")
    const q = query(certsRef, where("orgId", "==", orgId))
    const snap = await getDocs(q)
    const certificates = snap.docs?.map((d) => d.data())
    certificates.sort((a, b) => (b?.issueDate || "").localeCompare(a?.issueDate || ""))
    return { certificates, error: null }
  } catch (error) {
    return { certificates: [], error }
  }
}

// Certificates earned by a given learner (foundation for a future Skills Passport page)
export const listUserCertificates = async (userId) => {
  try {
    const certsRef = collection(db, "certificates")
    const q = query(certsRef, where("userId", "==", userId))
    const snap = await getDocs(q)
    const certificates = snap.docs?.map((d) => d.data())
    certificates.sort((a, b) => (b?.issueDate || "").localeCompare(a?.issueDate || ""))
    return { certificates, error: null }
  } catch (error) {
    return { certificates: [], error }
  }
}